import { ReactNode, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import AddIcon from '@material-ui/icons/Add';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import ViewColumnOutlinedIcon from '@material-ui/icons/ViewColumnOutlined';
import ViewListIcon from '@material-ui/icons/ViewList';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import {
  FluxButton,
  FluxPageHeader,
  FluxSearchInput,
  MONO_FONT,
  fluxTokens,
} from '../../flux';
import { Entity } from '@backstage/catalog-model';
import {
  CATALOG_FILTER_EXISTS,
  entityRouteParams,
} from '@backstage/plugin-catalog-react';
import {
  CATALOG_OPTIONS,
  CatalogScope,
  CatalogVariantOptions,
  useCatalogQuery,
  useCatalogUrlState,
} from './useCatalogQuery';
import { CatalogFilters } from './CatalogFilters';
import {
  CatalogFooter,
  CatalogResults,
  ColumnId,
  ColumnsMenu,
  applicableColumns,
  useColumnPrefs,
} from './CatalogResults';
import { kindLabels } from './presentation';

const useStyles = makeStyles(theme => {
  const t = fluxTokens(theme);
  return {
    page: {
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: t.pageBg,
      color: t.text,
      overflow: 'hidden',
      [theme.breakpoints.down('sm')]: { height: 'auto', overflow: 'visible' },
    },
    body: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      [theme.breakpoints.down('sm')]: { flexDirection: 'column' },
    },
    main: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    toolbar: {
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '10px 16px',
      borderBottom: `1px solid ${t.border}`,
      flexWrap: 'wrap',
    },
    search: { flex: 1, minWidth: 220, maxWidth: 420 },
    showing: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 7,
      fontSize: 12.5,
      color: t.textSecondary,
      whiteSpace: 'nowrap',
      '& b': { color: t.text, fontWeight: 600 },
    },
    liveDot: {
      width: 7,
      height: 7,
      borderRadius: '50%',
      background: t.accentStrong,
      boxShadow: `0 0 0 3px ${t.accentBg}`,
    },
    toolbarRight: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      marginLeft: 'auto',
    },
    viewToggle: {
      display: 'inline-flex',
      padding: 2,
      gap: 2,
      borderRadius: 8,
      border: `1px solid ${t.borderStrong}`,
      background: t.surface,
      '& > *': { border: 'none !important', height: 26, width: 28 },
    },
    error: {
      margin: 16,
      padding: '10px 12px',
      borderRadius: 8,
      fontSize: 12.5,
      color: t.danger,
      background: t.dangerBg,
      fontFamily: MONO_FONT,
    },
  };
});

const SCOPE_TITLES = { owned: 'Owned', starred: 'Starred', all: 'All' } as const;

type ListVariant = {
  options: CatalogVariantOptions;
  section: { label: string; to: string };
  title: (orgName: string) => string;
  labels?: { singular: string; plural: string; title: string };
  columnsKey: string;
  defaultColumns: ColumnId[];
  searchPlaceholder: string;
  rowHref?: (entity: Entity) => string;
  actions: (canCreate: boolean) => ReactNode;
};

const TECHDOCS_ANNOTATION = 'backstage.io/techdocs-ref';

const CATALOG_VARIANT: ListVariant = {
  options: CATALOG_OPTIONS,
  section: { label: 'Catálogo', to: '/catalog' },
  title: org => `${org} Software Catalog`,
  columnsKey: 'fluxstage.catalog.columns.v1',
  defaultColumns: ['system', 'owner', 'type', 'lifecycle'],
  searchPlaceholder: 'Buscar por nombre, sistema, owner o tags… ( / )',
  actions: canCreate => (
    <>
      {canCreate && (
        <FluxButton icon={<CloudUploadOutlinedIcon />} to="/catalog-import">
          Import Entity
        </FluxButton>
      )}
      <FluxButton variant="primary" icon={<AddIcon />} to="/create">
        Create Component
      </FluxButton>
    </>
  ),
};

const API_OPTIONS: CatalogVariantOptions = {
  defaultKind: 'api',
  fixedKind: true,
  defaultScope: 'all',
};

const API_VARIANT: ListVariant = {
  options: API_OPTIONS,
  section: { label: 'APIs', to: '/api-docs' },
  title: org => `${org} API Explorer`,
  columnsKey: 'fluxstage.apis.columns.v1',
  defaultColumns: ['system', 'owner', 'type', 'lifecycle'],
  searchPlaceholder: 'Buscar APIs por nombre, sistema u owner… ( / )',
  actions: canCreate =>
    canCreate ? (
      <FluxButton variant="primary" icon={<AddIcon />} to="/catalog-import">
        Register Existing API
      </FluxButton>
    ) : null,
};

const DOCS_OPTIONS: CatalogVariantOptions = {
  defaultKind: '',
  defaultScope: 'owned',
  baseFilter: { [`metadata.annotations.${TECHDOCS_ANNOTATION}`]: CATALOG_FILTER_EXISTS },
  clientPredicate: entity => Boolean(entity.metadata.annotations?.[TECHDOCS_ANNOTATION]),
};

const DOCS_VARIANT: ListVariant = {
  options: DOCS_OPTIONS,
  section: { label: 'Docs', to: '/docs' },
  title: org => `${org} Documentation`,
  labels: { singular: 'documento', plural: 'documentos', title: 'Docs' },
  columnsKey: 'fluxstage.docs.columns.v1',
  defaultColumns: ['kind', 'owner', 'type', 'tags'],
  searchPlaceholder: 'Buscar documentación por nombre, owner o tags… ( / )',
  rowHref: entity => {
    const p = entityRouteParams(entity);
    return `/docs/${p.namespace}/${p.kind}/${p.name}`;
  },
  actions: canCreate =>
    canCreate ? (
      <FluxButton icon={<CloudUploadOutlinedIcon />} to="/catalog-import">
        Register Existing
      </FluxButton>
    ) : null,
};

const FluxEntityListPage = ({ variant }: { variant: ListVariant }) => {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const orgName = configApi.getOptionalString('organization.name') ?? 'Backstage';
  const { allowed: canCreate } = usePermission({
    permission: catalogEntityCreatePermission,
  });

  const { state, update, reset } = useCatalogUrlState(variant.options);
  const [reloadKey, setReloadKey] = useState(0);
  const { result, kinds, facets, counts, ownershipReady } = useCatalogQuery(
    state,
    reloadKey,
    variant.options,
  );
  const [columnPrefs, setColumnPrefs] = useColumnPrefs(
    variant.columnsKey,
    variant.defaultColumns,
  );
  const [columnsAnchor, setColumnsAnchor] = useState<HTMLElement | null>(null);
  const columns = applicableColumns(state.kind, columnPrefs);
  const labels = variant.labels ?? (state.kind ? kindLabels(state.kind) : { singular: 'entidad', plural: 'entidades', title: 'Entities' });

  const [textInput, setTextInput] = useState(state.text);
  const pushedText = useRef(state.text);
  useEffect(() => {
    if (state.text !== pushedText.current) {
      pushedText.current = state.text;
      setTextInput(state.text);
    }
  }, [state.text]);
  useEffect(() => {
    if (textInput === pushedText.current) return undefined;
    const id = window.setTimeout(() => {
      pushedText.current = textInput;
      update({ text: textInput });
    }, 300);
    return () => window.clearTimeout(id);
  }, [textInput, update]);

  const fellBack = useRef(false);
  useEffect(() => {
    if (
      !fellBack.current &&
      ownershipReady &&
      !state.scopeExplicit &&
      state.scope === 'owned' &&
      variant.options.defaultScope === 'owned' &&
      counts.owned === 0
    ) {
      fellBack.current = true;
      update({ scope: 'all' });
    }
  }, [ownershipReady, state.scopeExplicit, state.scope, counts.owned, update, variant.options.defaultScope]);

  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (e.key === '/' && !typing) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const scopeTitle = `${SCOPE_TITLES[state.scope as CatalogScope]} ${labels.title}`;

  return (
    <div className={classes.page}>
      <FluxPageHeader
        crumbs={[{ label: 'Plataforma', to: '/' }, variant.section, { label: scopeTitle }]}
        title={variant.title(orgName)}
        count={`${counts.all ?? '…'} ${labels.plural}`}
        actions={
          <>
            <FluxButton
              iconOnly
              icon={<RefreshIcon />}
              tooltip="Recargar"
              onClick={() => setReloadKey(k => k + 1)}
            />
            {variant.actions(canCreate)}
          </>
        }
      />
      <div className={classes.body}>
        <CatalogFilters
          state={state}
          update={update}
          reset={() => {
            setTextInput('');
            reset();
          }}
          kinds={kinds}
          facets={facets}
          counts={counts}
          result={result}
          showKind={!variant.options.fixedKind}
          allowAnyKind={variant.options.defaultKind === ''}
          defaultScope={variant.options.defaultScope}
        />
        <div className={classes.main}>
          <div className={classes.toolbar}>
            <FluxSearchInput
              ref={searchRef}
              className={classes.search}
              value={textInput}
              onChange={setTextInput}
              placeholder={variant.searchPlaceholder}
            />
            <span className={classes.showing}>
              <span className={classes.liveDot} />
              Mostrando <b>{result.items.length}</b> de <b>{result.totalItems}</b>
            </span>
            <div className={classes.toolbarRight}>
              {state.view === 'list' && (
                <FluxButton
                  size="small"
                  icon={<ViewColumnOutlinedIcon />}
                  onClick={e => setColumnsAnchor(e.currentTarget)}
                >
                  Columnas
                </FluxButton>
              )}
              <div className={classes.viewToggle}>
                <FluxButton
                  size="small"
                  iconOnly
                  icon={<ViewListIcon />}
                  active={state.view === 'list'}
                  tooltip="Vista de lista"
                  onClick={() => update({ view: 'list' }, true)}
                />
                <FluxButton
                  size="small"
                  iconOnly
                  icon={<ViewModuleIcon />}
                  active={state.view === 'grid'}
                  tooltip="Vista de tarjetas"
                  onClick={() => update({ view: 'grid' }, true)}
                />
              </div>
            </div>
          </div>
          <ColumnsMenu
            kind={state.kind}
            columns={columnPrefs}
            onChange={setColumnPrefs}
            anchor={columnsAnchor}
            onClose={() => setColumnsAnchor(null)}
          />
          {result.error && (
            <div className={classes.error}>
              No se pudo consultar el catálogo: {result.error.message}
            </div>
          )}
          <CatalogResults
            state={state}
            result={result}
            columns={columns}
            update={update}
            rowHref={variant.rowHref}
            onReset={() => {
              setTextInput('');
              reset();
            }}
          />
          <CatalogFooter state={state} result={result} update={update} labels={labels} />
        </div>
      </div>
    </div>
  );
};

export const FluxCatalogPage = () => <FluxEntityListPage variant={CATALOG_VARIANT} />;
export const FluxApiExplorerPage = () => <FluxEntityListPage variant={API_VARIANT} />;
export const FluxDocsPage = () => <FluxEntityListPage variant={DOCS_VARIANT} />;
